import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { AutenticacaoService } from 'src/app/seguranca/autenticacao.service';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { NovoPlanoRenegociacao } from 'src/app/shared/models/novo-plano-renegociacao.model';
import { ResumoRenegociacao } from 'src/app/shared/models/resumo-renegociacao.model';
import { ToastService } from 'src/app/shared/toast';
import { RenegotiationResumeService } from '../renegotiation-resume.service';
import { ContratoService, SelectPlanNotFound } from 'src/app/contrato/contrato.service';

@Component({
  selector: 'app-renegotiation-resume-page',
  templateUrl: './renegotiation-resume-page.component.html',
  styleUrls: ['./renegotiation-resume-page.component.scss']
})
export class RenegotiationResumePageComponent implements OnInit {

  subscriptions = new Subscription();
  contractId: string;
  selectedPlanId: string;
  renegotiationResume: ResumoRenegociacao;
  isScrolledToEnd = false;
  acceptTerms = false;
  checkoutType: 'pix' | 'boleto' | null = null;
  hideScrollButton: boolean = false;
  isMobileMatches: Observable<BreakpointState> = this.breakpointObserver.observe('(max-width: 768px)');

  @ViewChild('terms') terms: ElementRef;
  selectedPlan: NovoPlanoRenegociacao;
  contractAlreadyDownloaded: boolean;
  permitirPagamentoPix: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loader: LoaderService,
    private renegotiationResumeService: RenegotiationResumeService,
    public breakpointObserver: BreakpointObserver,
    private toastService: ToastService,
    private autenticacaoService: AutenticacaoService,
    private contratoService: ContratoService,
  ) { }

  @ViewChild('scrollButton') scrollButton: ElementRef;
  showScrollButton = true;

  @HostListener('window:scroll', ['$event'])
  onWindowScroll() {
    const scrollPosition = window.pageYOffset;
    const documentHeight = document.body.scrollHeight;
    const offset = 680;
    this.showScrollButton = scrollPosition + window.innerHeight < documentHeight - offset;
  }

  ngOnInit(): void {
    this.subscriptions.add(this.route.params.subscribe(({ idPlano, idContrato }) => {
      this.contractId = idContrato;
      this.selectedPlanId = idPlano;
      this.fetchRenegotiationResume(idPlano, idContrato);
      this.verificarPermissaoBotaoPix(idContrato);
    }));
  }

  async fetchRenegotiationResume(idPlano: string, idContrato: string) {
    try {
      this.loader.start();

      this.renegotiationResume = await this.renegotiationResumeService.fetchRenegotiationResume(idContrato, idPlano);
      this.selectedPlan = this.renegotiationResumeService.getSelectedPlan(idPlano);
    } catch (errorResponse) {
      if (errorResponse instanceof SelectPlanNotFound) {
        this.toastService.show({ type: 'error', title: 'Plano não encontrado.', text: 'Por favor tente novamente mais tarde.' });
        this.router.navigate([`/portal/contratos/${this.contractId}/renegociacao`]);
        return;
      }

      if (errorResponse && errorResponse.error) {
        this.toastService.show({ type: 'error', text: errorResponse.error.mensagem, title: 'Erro ao buscar o acordo.' });
      } else {
        this.toastService.show({ type: 'error', title: 'Erro ao buscar o acordo. ', text: 'Por favor tente novamente mais tarde.' });
      }
    } finally {
      this.loader.stop();
    }
  }

  async downloadRenegotiationContract() {
    try {
      this.loader.start();
      await this.renegotiationResumeService.downloadRenegociantionContract(this.renegotiationResume);
      this.contractAlreadyDownloaded = true;
    } catch (ErroResponse) {
      if (ErroResponse && ErroResponse.error) {
        this.toastService.show({ type: 'error', text: ErroResponse.error.mensagem, title: 'Ops, algo deu errado!' });
      } else {
        this.toastService.show({ type: 'error', title: 'Erro ao efetuar download do contrato.', text: 'Por favor tente novamente mais tarde.' });
      }
    } finally {
      this.loader.stop();
    }
  }

  async efetivarAcordo() {
    try {
      this.loader.start();

      await this.renegotiationResumeService.confirmRenegotiation(this.contractId, this.selectedPlan);

      if (this.checkoutType === 'pix') { 
        this.router.navigate([`/portal/contratos/${this.contractId}/renegociacao/${this.selectedPlan.id}/checkout/pix`]);
      }

      if (this.checkoutType === 'boleto') { 
        this.router.navigate([`/portal/contratos/${this.contractId}/renegociacao/${this.selectedPlan.id}/checkout/boleto`]);
      }
      this.toastService.show({ type: 'success', title: 'Plano confirmado com sucesso!', text: 'Agora você podera efetuar o pagamento da entrada.' })
    } catch (response) {
      if (response && response.error) {
        this.toastService.show({ type: 'error', text: response.error.mensagem, title: 'Erro ao confirmar plano.' });
      } else {
        this.toastService.show({ type: 'error', title: 'Erro ao confirmar plano.', text: 'Por favor tente novamente mais tarde.' });
      }
    } finally {
      this.loader.stop();
    }
  }

  async verificarPermissaoBotaoPix(contratoId: string) {
    try {
      this.loader.start();
      const res = await this.contratoService.verificarElegibilidadePagamentoCeler(contratoId);
      this.permitirPagamentoPix = res.elegivel;
      this.checkoutType = this.permitirPagamentoPix ? 'pix' : 'boleto';
    } catch (error) {
      this.permitirPagamentoPix = false;
    } finally {
      this.loader.stop();
    }
  }

  scrollToEnd() {
    window.scroll({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth'
    });

    this.hideScrollButton = true;
  }

  handleEndScroll() {
    this.isScrolledToEnd = true;
  }

  handleCheckoutType(checkoutType: 'pix' | 'boleto') {
    this.checkoutType = checkoutType;
  }

  handleChangeAcceptTerms(event: MatCheckboxChange) {
    this.acceptTerms = event.checked;

    if (!event.checked) {
      return this.checkoutType = null;
    }

    if (!this.hasAuthenticatedEmployee) {
      this.checkoutType = 'pix';
    }
  }

  get hasAuthenticatedEmployee() {
    return this.autenticacaoService.temColaboradorAutenticado();
  }

  get disabledCheckoutTypeButton() {
    if (this.hasAuthenticatedEmployee) {
      return !this.acceptTerms || !this.contractAlreadyDownloaded;
    }
    return !this.acceptTerms;
  }

  get disabledPaymentButton() {
    if (this.hasAuthenticatedEmployee) {
      return !this.acceptTerms || !this.contractAlreadyDownloaded || this.checkoutType === null;
    }

    return !this.acceptTerms || this.checkoutType === null;
  }

  get checkoutButtonTooltipText() {
    if (this.hasAuthenticatedEmployee) {
      return 'O botão de forma de pagamento só estará disponível após a aceitação dos termos e download do contrato.';
    }

    return 'O botão de forma de pagamento só estará disponível após a aceitação dos termos.';
  }

  get paymentButtonTooltipText() {
    if (this.hasAuthenticatedEmployee) {
      return 'O botão de pagamento só estará disponível após a aceitação dos termos e download do contrato.';
    }

    return 'O botão de pagamento só estará disponível após a aceitação dos termos.';
  }
}
